/**
 * @name project-gallery.js
 * @description Basic, standalone widget for utilizing the Widen API to 
 *              render a grouping of project assets.
 * @example HTML markup.
 * 
 * <div 
 *   class="project-gallery"
 *   data-endpoint="assets/search"
 *   data-query="acn:{ThermaTru Product Literature}"
 *   data-expand="embeds,metadata,thumbnails"
 *   data-limit="100"
 *   data-scroll="true"
 *   data-sort="-last_update_date"
 * ></div>
 * <script src="widen.js" async defer></script>
 */

(function() {

  "use strict";

	// Set some constants upfront for ease of access.
	const settings = {
    authHeaderType: "Bearer",
		authKeys : {
			1: "outdoorsandsecurity/8ce161918deba406077466806f4fd00c"
		},
		vaultInstances: document.querySelectorAll(".project-gallery"),
		cacheExpiry: 30 * 60 // 30 minutes.
  };

	// Run init on our vaultInstances.
	settings.vaultInstances.forEach(vaultInstance => init(vaultInstance));

	/**
	 * @function randomKey()
	 * @description Takes an object of multiple authKeys and returns a single key.
	 * @param {Object} authKeys Object of multiple authKeys.
	 * @returns {String} Random key from the authKeys object.
	 */

	function randomKey(authKeys) {
		var keys = Object.keys(authKeys);
		return authKeys[keys[keys.length * Math.random() << 0]];;
	}

	/**
	 * @function init()
	 * @description Takes an HTMLElement and inserts HTML either containing collectionItems from the Widen API or errorMessage.
	 * @param {HTMLElement} vaultInstance Element where HTML will be inserted.
	 */

	function init(vaultInstance) {

		// Assemble our full Widen endpoint URL.
    const endpointUrl = 
		  `https://api.widencollective.com/v2/${vaultInstance.dataset.endpoint}` +
			`?query=${vaultInstance.dataset.query}` +
			`&expand=${vaultInstance.dataset.expand}` +
			`&limit=${vaultInstance.dataset.limit}` +
			`&scroll=${vaultInstance.dataset.scroll}` +
			// If we're sorting by a meta field, we do our sort inside the fetch rather than relying on the API.
			`&sort=${vaultInstance.dataset.sort == 'meta' ? "" : vaultInstance.dataset.sort}`;
			

		// Set our request payload.
		const requestOptions = {
			headers: {
				"Authorization": settings.authHeaderType + " " + randomKey(settings.authKeys),
				"Access-Control-Allow-Origin": "*"
			},
			method: "GET",
			redirect: "follow"
		};

		// Set some easy-access variables for our caching mechanism.

		const cached = localStorage.getItem(endpointUrl),
		      whenCached = localStorage.getItem(endpointUrl + ':ts'),
				  cacheAge = (Date.now() - whenCached) / 1000;

		// If we have a cached version, use it.
		if (cached !== null && whenCached !== null) {

			// Check the age of the cache to make sure it's not too stale.
			if (cacheAge < settings.cacheExpiry) {
				renderHTML(JSON.parse(cached));
			} else {
				// Clear the cache if it's stale.
				localStorage.removeItem(endpointUrl)
				localStorage.removeItem(endpointUrl + ':ts')
			}
		
		// Else, query the API and grab the data for rendering/caching.
		} else {
			// Fetch using our assembled endpoint URL and request payload.
			fetch(endpointUrl, requestOptions)
			.then(response => response.json())
			.then(function(response) {
				localStorage.setItem(endpointUrl, JSON.stringify(response));
				localStorage.setItem(endpointUrl + ':ts', Date.now());
				renderHTML(response);
			})
			// Catch the native error and send it to errorMessage.
			.catch(error => errorMessage(error, vaultInstance));
		}

		// This function renders the HTML in the above and does whatever custom sorting we want.
		function renderHTML(response) {
			// Check if a count greater than 0 is returned. If not, return an error message.

			if (response["total_count"] > 0) {

				var items = response.items;
        
				// Do whatever custom sorting we'd like.
				if (vaultInstance.dataset.sort == 'meta') {
					// Sort via our meta field, which should always be collectionOrder.
					items.sort(function(a, b){
						return a.metadata.fields.collectionOrder[0] - b.metadata.fields.collectionOrder[0];
					});
				}
				items.forEach(item => collectionItems(item, vaultInstance));
			} else {
				errorMessage("", vaultInstance);
			}
		}
	}

  /**
	 * @function errorMessage()
	 * @description Inserts error HTML in the provided HTMLElement.
	 * @param {String} error Error message.
	 * @param {HTMLElement} vaultInstance Element where HTML will be inserted.
	 */

	function errorMessage(error, vaultInstance){
		
		// Define HTML with template literals.
		const template = `
		  <div class="alert alert-danger" role="alert">
				<h3>Please <a href="/pages/contact-us">contact support</a>.</p>
				${error ? `<code>${error}</code>` : ""}
			</div>
		`;

		// Append HTML on element.
		vaultInstance.insertAdjacentHTML("beforeEnd", template);
	}

	/**
	 * @function collectionItems()
	 * @description Inserts asset HTML using the JSON data returned from the Widen API in the provided HTMLElement.
	 * @param {JSON} item JSON array containing asset-level data returned from the Widen API.
	 * @param {HTMLElement} vaultInstance Element where HTML will be inserted.
	 */

	function collectionItems(item, vaultInstance){

		// Set some variables for ease of access.
		let thumbnail   = item.thumbnails["600px"].url,
			  description = item.metadata.fields.description[0],
		    fullSize    = 'https://outdoorsandsecurity.widen.net/content/' + item.external_id	+ '/jpeg/' + item.filename + '?quality=80&w=1000',
				available   = item.released_and_not_expired,
				id          = item.id;

		// Define HTML with template literals.
		const template = `
			<div class="project-gallery-block" data-image-id="${id}">
				<div class="project-gallery-item">
					<a href="${fullSize}" target="_blank">
						<div class="project-gallery-image">
							<img src="${thumbnail}" alt="${description}" loading="lazy" />
						</div>	
						<strong class="project-gallery-description">
							${description}
						</strong>
					</a>
				</div>
			</div>
		`;

		if (available) {
			vaultInstance.insertAdjacentHTML("beforeEnd", template);
		}
  }  
	
})();
